import { api } from "Requests/Api";
import { useAppContext } from "State/Global/AppContext";
import { TreeSelect, Typography } from "antd";
import { DefaultOptionType } from "antd/lib/select";
import {
    ConversationLabel,
    ConversationTag,
    ConversationTagGroup,
} from "frontend-lib/DomainTypes/Conversation";
import useConvTags from "frontend-lib/Hooks/useConvTags";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

type Props = {
    convTags?: ConversationTag[];
    convTagsGroups?: ConversationTagGroup[];
};

function buildFlatTreeData(convTags: ConversationLabel[]): DefaultOptionType[] | undefined {
    return convTags.map((tag) => {
        return {
            value: tag.id,
            title: tag.value,
            label: tag.value,
        };
    });
}
function buildTreeData(
    convTags: ConversationLabel[] | undefined,
    convTagsGroups: ConversationTagGroup[] | undefined
): DefaultOptionType[] | undefined {
    if (!convTags) return;

    if (!convTagsGroups) return buildFlatTreeData(convTags);

    const groupRootNodes = convTagsGroups.map((g) => {
        return {
            value: g.id,
            title: <Text type="secondary">{g.name}</Text>,
            label: g.name,
        };
    });

    const rootNodes = groupRootNodes.map((root) => {
        const children = convTags
            .filter((tag) => tag.groupId === root.value)
            .map((tag) => {
                return {
                    value: tag.id,
                    title: tag.value,
                    label: tag.value,
                };
            });
        return {
            ...root,
            value: `groupId:${root.value}`, // This is done to differentiate a groupId from a tagId
            children,
        };
    });
    const orphanTagsNodes = convTags
        .filter((tag) => tag.groupId === undefined)
        .map((tag) => {
            return {
                value: tag.id,
                title: tag.value,
                label: tag.value,
            };
        });
    return [...rootNodes, ...orphanTagsNodes];
}

export default function ConvTagTreeSelect({ convTags, convTagsGroups }: Props) {
    //@ts-ignore
    const { appState } = useAppContext();

    const [treeData, setTreeData] = useState<DefaultOptionType[]>();
    const [selectedConvTagsId, setSelectedConvTagsId] = useState<number[]>();
    const [allConvTags, allConvTagsGroups, convTagsLoading, convTagError] = useConvTags({ api });
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();

    const selectedChatId = useMemo(() => {
        return appState?.selectedChat?.id;
    }, [appState?.selectedChat]);

    useEffect(() => {
        setTreeData(buildTreeData(allConvTags, allConvTagsGroups));
        setSelectedConvTagsId(convTags?.map((t) => t.conversationLabel.id));
    }, [allConvTags, allConvTagsGroups, convTags]);

    const handleSelectConvTag = async (tagId: number | string) => {
        if (typeof tagId == "string") return;

        setIsLoading(true);

        const newTag = {
            chatId: selectedChatId,
            conversationLabelId: tagId,
            labeler: "ASSISTANT",
        };

        await api.tags().addTagToConversation(newTag);
        setSelectedConvTagsId((tags) => (tags ? [...tags, tagId] : [tagId]));
        setIsLoading(false);
    };

    const handleDeselectConvTag = async (tagId: number | string) => {
        if (typeof tagId == "string") return;

        setIsLoading(true);

        await api.tags().deleteTagFromConversation(selectedChatId, tagId);
        setSelectedConvTagsId((tags) => (tags ? tags.filter((tId) => tId !== tagId) : undefined));
        setIsLoading(false);
    };

    return (
        <TreeSelect
            showSearch
            style={{ width: "100%" }}
            dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
            placeholder={t("labels.selectLabel")}
            allowClear
            multiple
            treeDefaultExpandAll
            onSelect={handleSelectConvTag}
            onDeselect={handleDeselectConvTag}
            treeData={treeData}
            popupClassName="selectPopUpMobile"
            value={selectedConvTagsId}
            loading={isLoading || convTagsLoading}
        />
    );
}
